import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Eye, EyeOff, ImageOff, Loader2, Lock, RefreshCw, ShieldAlert, UserRound, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Sidebar from '../components/layout/Sidebar';
import EmployerReviewCard from '../components/EmployerReviewCard';
import { organizationService, type OrganizationQueueItem } from '../services/organizationService';
import {
  categoryLabel,
  isAppealableTarget,
  isSevereCategory,
  isUnviewable,
  moderationService,
  splitCategories,
  targetLabel,
  type ModerationQueueItem,
} from '../services/moderationService';

type Tab = 'appeals' | 'blocked' | 'employers';

const TABS: ReadonlyArray<{ key: Tab; label: string; hint: string }> = [
  { key: 'appeals', label: 'Appeals', hint: 'A student asked us to look again. They are waiting on you.' },
  { key: 'blocked', label: 'Auto-blocked', hint: "Blocked by the automatic check, not appealed. Worth scanning for a filter that's wrong across the board." },
  {
    key: 'employers',
    label: 'Employers',
    hint: "Work domains the automatic check couldn't settle. A real business with little web presence looks the same as one that doesn't exist, so it asks rather than guesses — until you decide, the endorsement shows but earns no badge.",
  },
];

function relativeDate(iso: string | null): string {
  if (!iso) return 'unknown date';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown date';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Review queue for images the automatic filter held back.
 *
 * Two things about this page are deliberate and shouldn't be "simplified" away:
 *
 * 1. Every image is blurred until you click it. Reviewing is a job you do in
 *    an office, sometimes on a shared screen — the queue should never ambush
 *    you (or a demo audience) with what it contains.
 * 2. Anything the filter tagged `sexual/minors` is never rendered at all.
 *    See isUnviewable() in the service for why.
 */
export default function ContentReview() {
  const [items, setItems] = useState<ModerationQueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('appeals');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [images, employers] = await Promise.allSettled([moderationService.listQueue(), organizationService.listQueue()]);

      if (images.status === 'fulfilled') setItems(images.value.items ?? []);
      else throw images.reason;

      // The employer queue failing shouldn't blank the image queue — they're
      // independent jobs that happen to share a page.
      if (employers.status === 'fulfilled') {
        setOrgs(employers.value.items ?? []);
        setOrgError(null);
      } else {
        setOrgs([]);
        setOrgError(employers.reason instanceof Error ? employers.reason.message : 'Failed to load the employer queue');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the review queue');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const [orgs, setOrgs] = useState<OrganizationQueueItem[]>([]);
  const [orgError, setOrgError] = useState<string | null>(null);

  const appeals = useMemo(() => items.filter((i) => i.status === 'REVIEW_REQUESTED'), [items]);
  const blocked = useMemo(() => items.filter((i) => i.status !== 'REVIEW_REQUESTED'), [items]);
  const visible = tab === 'appeals' ? appeals : blocked;

  // Drop the row as soon as it's decided — it's no longer queue work.
  const onDecided = (item: ModerationQueueItem) => {
    setItems((prev) => prev.filter((i) => !(i.target === item.target && i.id === item.id)));
  };

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Sidebar />

      <div className="ml-[230px] mt-6 px-8 flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-gray-900 text-2xl font-bold">Content review</h2>
            <p className="text-gray-500 text-base font-normal max-w-3xl">
              Images the automatic check held back. Approving publishes the image on the student's public page; rejecting keeps it hidden for good.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={isLoading}
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-2">
          <div className="flex gap-1 border-b border-gray-200">
            {TABS.map((t) => {
              const count = t.key === 'appeals' ? appeals.length : t.key === 'blocked' ? blocked.length : orgs.length;
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                    isActive ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {t.label}
                  {count > 0 && (
                    <span
                      className={`ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                        t.key === 'appeals' || t.key === 'employers' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-500">{TABS.find((t) => t.key === tab)?.hint}</p>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────── */}
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</p>
        ) : tab === 'employers' ? (
          orgError ? (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{orgError}</p>
          ) : orgs.length === 0 ? (
            <EmptyState tab={tab} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {orgs.map((o) => (
                <EmployerReviewCard key={o.id} item={o} onDecided={() => setOrgs((prev) => prev.filter((x) => x.id !== o.id))} />
              ))}
            </div>
          )
        ) : visible.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
            {visible.map((item) => (
              <ReviewCard key={`${item.target}:${item.id}`} item={item} onDecided={() => onDecided(item)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ tab }: { tab: Tab }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-24 text-center">
      <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
        <Check className="w-6 h-6 text-emerald-600" />
      </div>
      <p className="text-gray-900 font-semibold">
        {tab === 'appeals' ? 'No appeals waiting' : tab === 'employers' ? 'No employers to check' : 'Nothing auto-blocked'}
      </p>
      <p className="text-sm text-gray-500 max-w-sm">
        {tab === 'appeals'
          ? 'When a student asks us to re-check an image, it shows up here first.'
          : tab === 'employers'
            ? 'Every work domain so far settled automatically. One only lands here when the check genuinely can’t tell.'
            : 'The automatic check hasn’t held anything back that’s still undecided.'}
      </p>
    </div>
  );
}

function ReviewCard({ item, onDecided }: { item: ModerationQueueItem; onDecided: () => void }) {
  const [revealed, setRevealed] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [imageBroken, setImageBroken] = useState(false);

  const categories = splitCategories(item.categories);
  const blocked = isUnviewable(item.categories);
  const isAppeal = item.status === 'REVIEW_REQUESTED';

  const decide = async (action: 'approve' | 'reject') => {
    if (busy) return;
    setBusy(action);
    try {
      await moderationService[action](item.target, item.id, note.trim() || undefined);
      toast.success(action === 'approve' ? 'Approved — the image is now public.' : 'Rejected — the image stays hidden.');
      onDecided();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Couldn't ${action} this image`);
      setBusy(null);
    }
  };

  return (
    <div className={`rounded-2xl border overflow-hidden flex flex-col ${isAppeal ? 'border-amber-200' : 'border-gray-200'}`}>
      {/* ── Image ──────────────────────────────────────────────────────── */}
      <div className="relative bg-gray-900 h-56 flex items-center justify-center">
        {blocked ? (
          <div className="flex flex-col items-center gap-2 px-6 text-center">
            <Lock className="w-7 h-7 text-red-400" />
            <p className="text-sm font-bold text-red-200">Not displayable</p>
            <p className="text-xs text-red-100/80 leading-snug">
              The filter tagged this as sexual content involving a minor. It is not shown here. Uphold the block and escalate offline.
            </p>
          </div>
        ) : imageBroken || !item.imageUrl ? (
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <ImageOff className="w-7 h-7" />
            <p className="text-xs">Image unavailable</p>
          </div>
        ) : (
          <>
            <img
              src={item.imageUrl}
              alt=""
              onError={() => setImageBroken(true)}
              className={`max-h-full max-w-full object-contain transition-[filter] duration-150 ${revealed ? '' : 'blur-2xl scale-105'}`}
            />
            {!revealed && (
              <button
                type="button"
                onClick={() => setRevealed(true)}
                className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-white/90 hover:text-white bg-black/30"
              >
                <Eye className="w-6 h-6" />
                <span className="text-xs font-semibold">Click to view</span>
              </button>
            )}
            {revealed && (
              <button
                type="button"
                onClick={() => setRevealed(false)}
                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/60 text-white text-[11px] font-medium hover:bg-black/80"
              >
                <EyeOff className="w-3.5 h-3.5" />
                Hide
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Detail ─────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                {targetLabel(item.target)}
              </span>
            </div>
            <p className="mt-1 text-sm font-semibold text-gray-900 truncate">{item.context || 'Untitled'}</p>
            <p className="text-xs text-gray-500 truncate">
              <UserRound className="inline w-3 h-3 mr-1 -mt-0.5" />
              {item.studentName || item.studentEmail || 'Unknown student'}
              {item.studentName && item.studentEmail ? ` · ${item.studentEmail}` : ''}
            </p>
          </div>
          <span className="shrink-0 text-[11px] text-gray-400">{relativeDate(item.checkedAt)}</span>
        </div>

        {/* Why it was held */}
        <div className="flex flex-wrap items-center gap-1.5">
          {categories.length === 0 ? (
            <span className="text-[11px] text-gray-400 italic">No category recorded</span>
          ) : (
            categories.map((c) => (
              <span
                key={c}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                  isSevereCategory(c) ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                }`}
              >
                {isSevereCategory(c) ? <ShieldAlert className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {categoryLabel(c)}
              </span>
            ))
          )}
        </div>

        {/* An outsider's upload with no owner to appeal it — say so, otherwise
            the missing student note reads as an appeal with a blank message. */}
        {!isAppealableTarget(item.target) && (
          <p className="text-[11px] text-gray-500 italic">Uploaded by an external verifier — no student appeal is possible for this image.</p>
        )}

        {/* The student's side of it — only present on an appeal */}
        {isAppeal && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <p className="text-[11px] font-bold text-amber-900">Student asked for a review</p>
            <p className="mt-0.5 text-xs text-amber-900/90 leading-snug">
              {item.studentReviewNote || <span className="italic opacity-70">They didn&rsquo;t add a note.</span>}
            </p>
          </div>
        )}

        {/* ── Decision ─────────────────────────────────────────────────── */}
        <div className="mt-auto flex flex-col gap-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Optional note (kept internally with the decision)"
            className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-xs outline-none focus:border-primary resize-none"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => decide('approve')}
              disabled={busy !== null || blocked}
              title={blocked ? 'Cannot be approved from this screen' : undefined}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Approve
            </button>
            <button
              type="button"
              onClick={() => decide('reject')}
              disabled={busy !== null}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Keep hidden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
